import Details from '@/components/Details/Details';
import InfoLabel from '@/components/InfoLabel';
import IEventType from '@/types/IEventType';
import {
    Avatar,
    Button,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    IconButton,
    Input,
    Select,
    VStack
} from '@chakra-ui/react';
import { FcAbout } from 'react-icons/fc';
import { MdDelete } from 'react-icons/md';
import CRSelect from 'react-select';
import LocationOption from './components/LocationOption';
import useSectionGeneral from './useSectionGeneral';
export interface ISectionGeneralProps {
	service: IEventType;
	onChange: (values: Partial<IEventType>) => void;
}

export default function SectionGeneral(props: ISectionGeneralProps) {
	const {
		handleChangeImageUrlClick,
		handleDeleteImageClick,
		onSubmit,
		registerLocationField,
		registerSlug,
		registerSubmitOnBlur,
		registerSubmitOnChange,
		t,
		errors,
		isLoading,
		isPhysicalLocationFieldVisible,
		link,
		locationOptions,
		shouldAutoComputeStep,
		values,
	} = useSectionGeneral(props);

	return (
		<Details image={<FcAbout />} title={t('General Information')}>
			<form onSubmit={onSubmit}>
				<FormControl p="1">
					<InfoLabel
						text={t('Booking page image')}
						help={t('Select an image to display on your booking page.')}
						htmlFor="page-image"
					/>
					<VStack>
						<Avatar size="xl" src={values.imageUrl}>
							{values.imageUrl ? (
								<IconButton
									onClick={handleDeleteImageClick}
									size="sm"
									position="absolute"
									top="0"
									right="-2"
									borderRadius="50%"
									colorScheme="red"
									aria-label={t('Delete')}
									icon={<MdDelete />}
								/>
							) : null}
						</Avatar>
						<Button type="button" size="sm" onClick={handleChangeImageUrlClick}>
							{t('Change image')}
						</Button>
					</VStack>
				</FormControl>

				<FormControl mb="1rem" isInvalid={!!errors.name}>
					<InfoLabel htmlFor="name" text={t('Name')} help={t('The name will be displayed on the booking page.')} />
					<Input id="name" {...registerSubmitOnBlur('name')} />
					<FormErrorMessage>{errors.name?.message}</FormErrorMessage>
				</FormControl>

				<FormControl mb="1rem" isInvalid={!!errors.description}>
					<InfoLabel
						htmlFor="description"
						text={t('Description')}
						help={t('Optionally add a description below the title of the service.')}
					/>
					<Input id="description" {...registerSubmitOnBlur('description')} />
					<FormErrorMessage>{errors.description?.message}</FormErrorMessage>
				</FormControl>

				<FormControl mb="1rem" id="duration">
					<InfoLabel htmlFor="duration" text={t('Duration')} help={t('Choose the event duration.')} />
					<Select {...registerSubmitOnChange('duration')}>
						<option value="PT10M">10m</option>
						<option value="PT15M">15m</option>
						<option value="PT20M">20m</option>
						<option value="PT30M">30m</option>
						<option value="PT45M">45m</option>
						<option value="PT1H">1h</option>
						<option value="PT1H30M">1h 30m</option>
						<option value="PT2H">2h</option>
						<option value="PT3H">3h</option>
						<option value="PT4H">4h</option>
						<option value="PT5H">5h</option>
					</Select>
				</FormControl>

				<FormControl mb="1rem">
					<InfoLabel text={t('Location')} help={t('Configure the meeting location')} htmlFor="location" />

					<CRSelect
						className="chakra-select"
						styles={{
							control: (baseStyles, state) => ({
								...baseStyles,
								paddingInlineStart: 'var(--chakra-space-2)',
								height: 'var(--chakra-sizes-10)',
								borderRadius: 'var(--chakra-radii-md)',
								borderColor: state.isFocused ? 'blue.400' : 'var(--chakra-colors-chakra-border-color)',
							}),
						}}
						inputId="location"
						defaultValue={locationOptions.find((option) => option.value === values.location)}
						{...registerLocationField()}
						options={locationOptions}
						components={{ Option: LocationOption }}
					/>

					{values.location === 'BOOKER_SELECTION' ? (
						<FormHelperText> {t('Edit available options on the location question.')} </FormHelperText>
					) : null}

					{values.location === 'BOOKER_PHONE' ? (
						<FormHelperText> {t('A phone question was added to the booking page.')} </FormHelperText>
					) : null}
				</FormControl>

				{isPhysicalLocationFieldVisible ? (
					<FormControl mb="1rem" isInvalid={!!errors.locationMeta}>
						<FormLabel htmlFor="locationMeta">{t('Custom location')}</FormLabel>
						<Input
							autoFocus
							id="locationMeta"
							{...registerSubmitOnBlur('locationMeta')}
							placeholder={t('Write custom location details here')}
						/>
						<FormErrorMessage> {errors.locationMeta?.message} </FormErrorMessage>
					</FormControl>
				) : null}

				<FormControl mb="1rem" isInvalid={!!errors.slug}>
					<InfoLabel
						htmlFor="slug"
						text={t('Booking page link')}
						help={t('Remember to notify your customers about the new link when you change it.')}
					/>

					<Input id="slug" {...registerSlug('slug')} />
					<FormHelperText>{link}</FormHelperText>
					<FormErrorMessage> {errors.slug?.message} </FormErrorMessage>
				</FormControl>

				<FormControl mb="1rem">
					<FormLabel htmlFor="step">{t('Start time frequency')}</FormLabel>
					<Select id="step" {...registerSubmitOnChange('step')} display={shouldAutoComputeStep ? 'none' : 'initial'}>
						<option value="PT5M">5m</option>
						<option value="PT10M">10m</option>
						<option value="PT15M">15m</option>
						<option value="PT20M">20m</option>
						<option value="PT30M">30m</option>
						<option value="PT1H">1h</option>
					</Select>
					<FormHelperText>
						<Checkbox {...registerSubmitOnChange('shouldAutoComputeStep')} isDisabled={isLoading} size="sm">
							{t('Calculate from duration')}
						</Checkbox>
					</FormHelperText>
				</FormControl>
			</form>
		</Details>
	);
}
